import { useCallback, useEffect, useMemo, useState } from "react";
import { Archive, BriefcaseBusiness, Building, ChevronRight, Edit2, Plus, RotateCcw, Search, Trash2, Users } from "lucide-react";
import Panel from "../../components/Panel";
import { businessApi } from "../../../services/api";
import { useBusinessAuth } from "../../context/BusinessAuthContext";
import { Button, ConfirmDialog, Dialog, Field, Input, Notice, Select, Textarea, plainError } from "../../../components/ui";
import HRPageShell from "./components/HRPageShell";

const LEVELS = [
  [100, "Level 100 - Business Administrator"],
  [85, "Level 85 - General / Store Manager"],
  [60, "Level 60 - Department Manager"],
  [40, "Level 40 - Officer / Specialist"],
  [20, "Level 20 - Staff"],
];

const roleName = (role) => role?.displayName || role?.display_name || role?.name || "Unnamed role";
const levelLabel = (value) => LEVELS.find(([id]) => Number(id) === Number(value))?.[1] || `Level ${value || "-"}`;
const activeMember = (member) => String(member?.status || "").toUpperCase() === "ACTIVE";

function StatusBadge({ status }) {
  const normalized = status === "inactive" ? "archived" : status;
  const cls = normalized === "active"
    ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400"
    : "bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400";
  return <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>{normalized}</span>;
}


/** Department details (C1 form: naming and a head are corrected the same way). Errors stay in the dialog. */
function DepartmentDialog({ department, members, onSave, onClose }) {
  const [form, setForm] = useState({
    name: department?.name || "",
    code: department?.code || "",
    description: department?.description || "",
    head: department?.head_member_id ? String(department.head_member_id) : "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));
  const submit = async (event) => {
    event.preventDefault();
    if (saving) return;
    if (!form.name.trim()) { setError("Enter the department name."); return; }
    setSaving(true);
    setError("");
    try {
      await onSave({
        name: form.name.trim(),
        code: form.code.trim() || null,
        description: form.description.trim() || null,
        head_member_id: form.head ? Number(form.head) : null,
      });
    } catch (err) {
      setError(plainError(err, "The department was not saved. Try again."));
      setSaving(false);
    }
  };
  return (
    <Dialog
      open
      onOpenChange={(isOpen) => { if (!isOpen && !saving) onClose(); }}
      title={department ? `Edit ${department.name}` : "New department"}
      footer={(
        <>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" type="submit" form="department-form" loading={saving}>{department ? "Save department" : "Create department"}</Button>
        </>
      )}
    >
      <form id="department-form" noValidate onSubmit={submit} className="space-y-4">
        <Field label="Department name" required><Input value={form.name} onChange={set("name")} maxLength={100} /></Field>
        <Field label="Code"><Input value={form.code} onChange={set("code")} maxLength={30} /></Field>
        <Field label="Description"><Textarea rows={3} maxLength={500} value={form.description} onChange={set("description")} /></Field>
        <Field label="Department head">
          <Select value={form.head} onChange={set("head")}>
            <option value="">None assigned</option>
            {members.filter(activeMember).map((member) => <option key={member.id} value={String(member.id)}>{member.name || member.email || "Employee"}</option>)}
          </Select>
        </Field>
        {error ? <Notice tone="danger" title="Not saved" role="alert">{error}</Notice> : null}
      </form>
    </Dialog>
  );
}

/**
 * Business role details. A new role, or a name/code/description change, saves directly (C1). Changing the authority
 * level of a role people already hold (C2) is reviewed first. The server updates the role only; whether existing
 * holders' recorded authority follows it is a completion-audit question, so the review does not claim it.
 */
function RoleDialog({ department, role, onSave, onClose }) {
  const [form, setForm] = useState({
    displayName: role ? roleName(role) : "",
    code: role?.code || "",
    level: role ? String(role.hierarchyLevel || role.hierarchy_level || "") : "",
    description: role?.description || "",
  });
  const [step, setStep] = useState("edit");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));
  const holders = Number(role?.assignedEmployeeCount || 0);
  const levelChanged = Boolean(role) && String(role.hierarchyLevel || role.hierarchy_level || "") !== form.level;

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      await onSave({
        displayName: form.displayName.trim(),
        code: form.code.trim() || null,
        description: form.description.trim(),
        departmentId: department.id,
        hierarchyLevel: Number(form.level),
      });
    } catch (err) {
      setError(plainError(err, "The business role was not saved. Try again."));
      setSaving(false);
      setStep("edit");
    }
  };
  const submit = (event) => {
    event.preventDefault();
    if (saving) return;
    if (!form.displayName.trim()) { setError("Enter the business role name."); return; }
    if (!form.level) { setError("Choose the authority level."); return; }
    if (levelChanged && holders > 0) { setStep("review"); return; }
    save();
  };

  return (
    <Dialog
      open
      onOpenChange={(isOpen) => { if (!isOpen && !saving) onClose(); }}
      title={step === "review" ? `Change the authority of ${roleName(role)}?` : role ? `Edit ${roleName(role)}` : `Add a role to ${department.name}`}
      footer={step === "review" ? (
        <>
          <Button onClick={() => setStep("edit")} disabled={saving}>Back to editing</Button>
          <Button variant="primary" loading={saving} onClick={save}>Change authority</Button>
        </>
      ) : (
        <>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" type="submit" form="role-form" loading={saving}>{role ? "Save role" : "Create role"}</Button>
        </>
      )}
    >
      {step === "review" ? (
        <div className="space-y-3 text-body-sm text-secondary">
          <dl className="divide-y divide-subtle border-y border-subtle">
            <div className="flex justify-between gap-4 py-2"><dt>Authority now</dt><dd className="font-medium text-foreground">{levelLabel(role.hierarchyLevel || role.hierarchy_level)}</dd></div>
            <div className="flex justify-between gap-4 py-2"><dt>New authority</dt><dd className="font-medium text-foreground">{levelLabel(form.level)}</dd></div>
            <div className="flex justify-between gap-4 py-2"><dt>People with this role</dt><dd className="font-medium text-foreground">{holders}</dd></div>
          </dl>
          <p>The role's authority level changes. Each person's own employment record is not changed by this save.</p>
        </div>
      ) : (
        <form id="role-form" noValidate onSubmit={submit} className="space-y-4">
          <Field label="Business role" required><Input value={form.displayName} onChange={set("displayName")} maxLength={100} /></Field>
          <Field label="Role code"><Input value={form.code} onChange={set("code")} maxLength={50} /></Field>
          <Field label="Authority level" required>
            <Select value={form.level} onChange={set("level")}>
              <option value="">Choose an authority level</option>
              {LEVELS.map(([value, label]) => <option key={value} value={String(value)}>{label}</option>)}
            </Select>
          </Field>
          <Field label="Description"><Textarea rows={3} maxLength={500} value={form.description} onChange={set("description")} /></Field>
          {error ? <Notice tone="danger" title="Not saved" role="alert">{error}</Notice> : null}
        </form>
      )}
    </Dialog>
  );
}

export default function HRDepartmentsFoundation() {
  const auth = useBusinessAuth();
  const authRole = String(auth?.role || "").toUpperCase();
  const hasAnyPermission = useCallback((permissionNames = []) => (
    authRole === "OWNER" || permissionNames.some((permissionName) => auth?.hasPermission?.(permissionName))
  ), [auth, authRole]);
  const canCreateDepartments = hasAnyPermission(["departments.create.business", "hr:departments:manage"]);
  const canUpdateDepartments = hasAnyPermission(["departments.update.business", "hr:departments:manage"]);
  const canArchiveDepartments = hasAnyPermission(["departments.archive.business", "hr:departments:manage"]);
  const canManageRoleCatalog = authRole === "OWNER";
  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [members, setMembers] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [expandedMembers, setExpandedMembers] = useState({});
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [deptRes, roleRes, memberRes] = await Promise.all([
        businessApi.getDepartments(),
        businessApi.getHRRoles(),
        businessApi.getMembers(),
      ]);
      setDepartments(Array.isArray(deptRes?.departments) ? deptRes.departments : []);
      setRoles((Array.isArray(roleRes?.data) ? roleRes.data : []).filter((role) => role.name !== "OWNER"));
      setMembers(Array.isArray(memberRes?.data) ? memberRes.data : []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const rolesByDept = useMemo(() => roles.reduce((map, role) => {
    const key = String(role.departmentId || role.department_id || "unassigned");
    if (!map[key]) map[key] = [];
    map[key].push(role);
    return map;
  }, {}), [roles]);

  const filtered = useMemo(() => departments.filter((department) => {
    const haystack = [
      department.name,
      department.code,
      department.description,
      department.head_name,
      ...(rolesByDept[String(department.id)] || []).map((role) => `${roleName(role)} ${role.code || ""}`),
    ].join(" ").toLowerCase();
    return haystack.includes(query.trim().toLowerCase());
  }), [departments, rolesByDept, query]);

  const toggleExpand = async (departmentId) => {
    if (expandedId === departmentId) return setExpandedId(null);
    setExpandedId(departmentId);
    if (expandedMembers[departmentId]) return;
    try {
      const res = await businessApi.getDepartmentMembers(departmentId);
      setExpandedMembers((current) => ({ ...current, [departmentId]: Array.isArray(res?.members) ? res.members : Array.isArray(res?.data) ? res.data : [] }));
    } catch {
      setExpandedMembers((current) => ({ ...current, [departmentId]: [] }));
    }
  };

  // Forms and confirmations are shared Dialog / ConfirmDialog; a refusal stays in them in the server's words.
  const [dialog, setDialog] = useState(null);
  const openDepartmentForm = (department = null) => {
    if (department?.id ? !canUpdateDepartments : !canCreateDepartments) return;
    setDialog({ kind: "department", department });
  };
  const openRoleForm = (department, role = null) => setDialog({ kind: "role", department, role });
  const archiveDepartment = (department) => { if (canArchiveDepartments) setDialog({ kind: "archive-department", department }); };
  const archiveRole = (role) => setDialog({ kind: "archive-role", role });
  // APP-HR-080: an archived department can be restored; one that was never used can be deleted. The server decides
  // both again when asked, and names what blocks it.
  const restoreDepartment = (department) => { if (canUpdateDepartments) setDialog({ kind: "restore-department", department }); };
  const deleteDepartment = (department) => { if (canArchiveDepartments) setDialog({ kind: "delete-department", department }); };
  const close = () => setDialog(null);
  const done = async () => { setDialog(null); await load(); };

  const saveDepartment = async (data) => {
    if (dialog.department?.id) await businessApi.updateDepartment(dialog.department.id, data);
    else await businessApi.createDepartment(data);
    await done();
  };
  const saveRole = async (data) => {
    if (dialog.role?.id) await businessApi.updateRoleMeta(dialog.role.id, data);
    else await businessApi.createRole({ ...data, permissionIds: [] });
    await done();
  };

  return (
    <HRPageShell
      title="Departments & Business Roles"
      subtitle="Shape the organization by defining departments, the business roles inside them, and the structural foundation employees inherit during assignment."
      badges={[
        `${filtered.length} departments in view`,
        `${roles.length} business roles`,
        `${members.length} employees loaded`,
      ]}
    >
    <Panel
      title="Departments & Business Roles"
      subtitle="Departments now own their business roles directly, making assignment and permission design easier to maintain."
      contentClassName="space-y-4"
    >
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-[220px] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search departments or business roles..." className="w-full rounded-xl border border-neutral-200 bg-white py-2 pl-9 pr-3 text-sm dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100" />
          </div>
          {canCreateDepartments && (
            <button onClick={() => openDepartmentForm()} className="business-primary-action inline-flex items-center gap-2 rounded-xl bg-neutral-900 px-4 py-2 text-sm font-medium text-white dark:bg-white dark:text-neutral-900">
              <Plus className="h-4 w-4" /> New Department
            </button>
          )}
        </div>
        <div className="w-full rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-2 text-xs leading-5 text-neutral-600 dark:border-neutral-800 dark:bg-neutral-950/30 dark:text-neutral-300">
          Departments now directly own their business roles and structural hierarchy. Department create, update, and archive actions can now follow their explicit HR permissions, while the role catalog itself stays owner-managed.
        </div>
      </div>

      {loading ? (
        <div className="py-12 text-center text-sm text-neutral-400">Loading organizational structure...</div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-neutral-200 dark:border-neutral-800" style={{ scrollbarGutter: "auto" }}>
          <table className="w-full min-w-[920px] text-left text-sm">
            <thead className="border-b bg-neutral-50 text-xs uppercase tracking-wide text-neutral-500 dark:border-neutral-700 dark:bg-neutral-800/40">
              <tr>
                <th className="px-4 py-3">Department</th>
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3">Head</th>
                <th className="px-4 py-3 text-center">Members</th>
                <th className="px-4 py-3 text-center">Roles</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y dark:divide-neutral-800">
              {filtered.map((department) => {
                const departmentRoles = rolesByDept[String(department.id)] || [];
                const currentMembers = expandedMembers[department.id] || [];
                return (
                  <tr key={department.id} className="align-top">
                    <td className="px-4 py-3">
                      <button onClick={() => toggleExpand(department.id)} className="flex items-center gap-2 font-medium text-neutral-900 hover:underline dark:text-neutral-100">
                        <Building className="h-4 w-4 text-neutral-400" />
                        {department.name}
                        <ChevronRight className={`h-3.5 w-3.5 text-neutral-400 transition ${expandedId === department.id ? "rotate-90" : ""}`} />
                      </button>
                      {department.description && <p className="mt-1 text-xs text-neutral-400">{department.description}</p>}
                      {expandedId === department.id && (
                        <div className="mt-4 ml-6 grid gap-3 lg:grid-cols-2">
                          <div className="rounded-xl border border-neutral-200 p-3 dark:border-neutral-800">
                            <div className="mb-2 inline-flex items-center gap-1 text-sm font-semibold text-neutral-700 dark:text-neutral-200"><Users className="h-4 w-4" /> Members</div>
                            {!currentMembers.length ? <div className="text-xs text-neutral-500 dark:text-neutral-400">No assigned employees.</div> : currentMembers.map((member) => (
                              <div key={member.id} className="mb-2 text-xs text-neutral-600 dark:text-neutral-300">
                                <span className="font-medium text-neutral-900 dark:text-neutral-100">{[member.first_name, member.last_name].filter(Boolean).join(" ") || member.email}</span>
                                <span className="ml-2 text-neutral-400">{member.designation || member.business_role_name || "Unassigned role"}</span>
                              </div>
                            ))}
                          </div>
                          <div className="rounded-xl border border-neutral-200 p-3 dark:border-neutral-800">
                            <div className="mb-2 flex items-center justify-between gap-2">
                              <span className="inline-flex items-center gap-1 text-sm font-semibold text-neutral-700 dark:text-neutral-200"><BriefcaseBusiness className="h-4 w-4" /> Business Roles</span>
                              {canManageRoleCatalog && <button onClick={() => openRoleForm(department)} className="rounded-lg border border-neutral-200 px-2 py-1 text-[11px] font-medium dark:border-neutral-700 dark:text-neutral-300">Add Role</button>}
                            </div>
                            {!departmentRoles.length ? <div className="text-xs text-neutral-500 dark:text-neutral-400">No business roles configured.</div> : departmentRoles.map((role) => (
                              <div key={role.id} className="mb-2 rounded-xl border border-neutral-200 px-3 py-2 dark:border-neutral-800">
                                <div className="flex items-start justify-between gap-2">
                                  <div>
                                    <div className="font-medium text-neutral-900 dark:text-neutral-100">{roleName(role)}</div>
                                    <div className="text-[11px] text-neutral-500 dark:text-neutral-400">{levelLabel(role.hierarchyLevel || role.hierarchy_level)}</div>
                                    <div className="text-[11px] text-neutral-400 dark:text-neutral-500">{role.code || "No code"} • {Number(role.assignedEmployeeCount || 0)} employees • {Number(role.pendingInvitationCount || 0)} invites</div>
                                  </div>
                                  {canManageRoleCatalog && <div className="flex items-center gap-1">
                                    <button onClick={() => openRoleForm(department, role)} className="rounded p-1 hover:bg-neutral-100 dark:hover:bg-neutral-800" title="Edit business role"><Edit2 className="h-3.5 w-3.5 text-neutral-400" /></button>
                                    <button onClick={() => archiveRole(role)} className="rounded p-1 hover:bg-red-50 dark:hover:bg-red-950/20" title="Archive business role"><Archive className="h-3.5 w-3.5 text-red-400" /></button>
                                  </div>}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-neutral-500 dark:text-neutral-400">{department.code || "—"}</td>
                    <td className="px-4 py-3 text-neutral-700 dark:text-neutral-300">{department.head_name || "—"}</td>
                    <td className="px-4 py-3 text-center text-neutral-700 dark:text-neutral-300">{department.member_count}</td>
                    <td className="px-4 py-3 text-center text-neutral-700 dark:text-neutral-300">{Number(department.role_count || department.roleCount || departmentRoles.length)}</td>
                    <td className="px-4 py-3"><StatusBadge status={department.status} /></td>
                    <td className="px-4 py-3 text-center">
                      <div className="inline-flex gap-1">
                        {canUpdateDepartments && <button onClick={() => openDepartmentForm(department)} className="rounded p-1 hover:bg-neutral-100 dark:hover:bg-neutral-800" title="Edit department"><Edit2 className="h-4 w-4 text-neutral-400" /></button>}
                        {canManageRoleCatalog && <button onClick={() => openRoleForm(department)} className="rounded p-1 hover:bg-neutral-100 dark:hover:bg-neutral-800" title="Add business role"><Plus className="h-4 w-4 text-neutral-400" /></button>}
                        {canArchiveDepartments && String(department.status) === "active" ? <button onClick={() => archiveDepartment(department)} className="rounded p-1 hover:bg-red-50 dark:hover:bg-red-950/20" title="Archive department" aria-label={`Archive ${department.name}`}><Archive className="h-4 w-4 text-red-400" /></button> : null}
                        {canUpdateDepartments && String(department.status) !== "active" ? <button onClick={() => restoreDepartment(department)} className="rounded p-1 hover:bg-neutral-100 dark:hover:bg-neutral-800" title="Restore department" aria-label={`Restore ${department.name}`}><RotateCcw className="h-4 w-4 text-neutral-500" /></button> : null}
                        {canArchiveDepartments && Number(department.never_used) === 1 ? <button onClick={() => deleteDepartment(department)} className="rounded p-1 hover:bg-red-50 dark:hover:bg-red-950/20" title="Delete department (never used)" aria-label={`Delete ${department.name}`}><Trash2 className="h-4 w-4 text-red-400" /></button> : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
    {dialog?.kind === "department" ? <DepartmentDialog department={dialog.department} members={members} onSave={saveDepartment} onClose={close} /> : null}
    {dialog?.kind === "role" ? <RoleDialog department={dialog.department} role={dialog.role} onSave={saveRole} onClose={close} /> : null}
    {dialog?.kind === "archive-department" ? (
      <ConfirmDialog
        title={`Archive ${dialog.department.name}?`}
        facts={[{ label: "Business roles in it", value: String((rolesByDept[String(dialog.department.id)] || []).length) }]}
        consequence="It can no longer be chosen for anyone or any role. It must have no employees, business roles or departments under it; its history stays, and it can be restored."
        confirmLabel="Archive department"
        cancelLabel="Keep department"
        tone="danger"
        failureTitle="Not archived"
        onConfirm={async () => { await businessApi.deactivateDepartment(dialog.department.id); await done(); }}
        onClose={close}
      />
    ) : null}
    {dialog?.kind === "restore-department" ? (
      <ConfirmDialog
        title={`Restore ${dialog.department.name}?`}
        consequence="It becomes available again for employees and business roles. Nothing else changes."
        confirmLabel="Restore department"
        cancelLabel="Keep archived"
        failureTitle="Not restored"
        onConfirm={async () => { await businessApi.restoreDepartment(dialog.department.id); await done(); }}
        onClose={close}
      />
    ) : null}
    {dialog?.kind === "delete-department" ? (
      <ConfirmDialog
        title={`Delete ${dialog.department.name}?`}
        consequence="This department has never been used, so it is removed for good and its name becomes free. This cannot be undone. A department that has ever had employees or roles cannot be deleted; it is archived instead."
        confirmLabel="Delete department"
        cancelLabel="Keep department"
        tone="danger"
        failureTitle="Not deleted"
        onConfirm={async () => { await businessApi.deleteDepartment(dialog.department.id); await done(); }}
        onClose={close}
      />
    ) : null}
    {dialog?.kind === "archive-role" ? (
      <ConfirmDialog
        title={`Archive the ${roleName(dialog.role)} role?`}
        facts={[{ label: "People with this role", value: String(Number(dialog.role.assignedEmployeeCount || 0)) }]}
        consequence="It stays in history but can no longer be given to anyone. People who hold it must move to another role first."
        confirmLabel="Archive role"
        cancelLabel="Keep role"
        tone="danger"
        failureTitle="Not archived"
        onConfirm={async () => { await businessApi.deleteRole(dialog.role.id); await done(); }}
        onClose={close}
      />
    ) : null}
    </HRPageShell>
  );
}
